import type {
  UsFindingValue,
  UsSide,
  UsSidedAnswer,
  UsWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { Box, Chip, MenuItem, TextField, Typography } from "@material-ui/core";
import { ChipRow, chipStyle } from "../../epocus-chip";
import { EPOCUS_TEXT_SECONDARY, rem } from "../../epocus-palette";
import { otherTextKey } from "../worksheet-field-defs";
import {
  AllNormalToggle,
  EnumChipSelect,
  NotesField,
  NumericInput,
  ViewChecklist,
  WorksheetSection,
} from "../worksheet-primitives";
import {
  INDICATION_OTHER_KEY,
  instanceLabel,
  isUsAllNormal,
  normalImpressionLabel,
  normalizeUs,
  selectedValues,
  toggleUsAllNormal,
  type UsSectionInstance,
  usDefinitionFor,
  visibleInstances,
} from "./us-definitions";
import { OTHER, type UsFieldDef, type UsOption } from "./us-fields";

const SIDE_OPTIONS: { value: UsSide; label: string }[] = [
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
  { value: "both", label: "Both" },
];

const FieldLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

const FieldHeading: FC<{ children: string }> = ({ children }) => (
  <Typography style={{ fontSize: rem(14), fontWeight: 700, marginTop: 8 }}>
    {children}
  </Typography>
);

const MultiChipSelect: FC<{
  options: readonly UsOption[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  disabled: boolean;
  label: string;
}> = ({ options, selected, onChange, disabled, label }) => (
  <ChipRow role="group" aria-label={label}>
    {options.map((option) => {
      const isSelected = selected.includes(option.value);
      return (
        <Chip
          key={option.value}
          label={option.label}
          variant="outlined"
          aria-pressed={isSelected}
          style={chipStyle(isSelected)}
          onClick={() =>
            onChange(
              isSelected
                ? selected.filter((value) => value !== option.value)
                : [...selected, option.value],
            )
          }
          disabled={disabled}
        />
      );
    })}
  </ChipRow>
);

const FreeText: FC<{
  value: UsFindingValue | undefined;
  onChange: (value: string | null) => void;
  label: string;
  disabled: boolean;
}> = ({ value, onChange, label, disabled }) => (
  <TextField
    fullWidth
    size="small"
    variant="outlined"
    value={typeof value === "string" ? value : ""}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(e.target.value || null)
    }
    disabled={disabled}
    inputProps={{ "aria-label": label }}
  />
);

const toMultiValue = (
  options: readonly UsOption[],
  previous: UsFindingValue | undefined,
  next: readonly string[],
): (string | UsSidedAnswer)[] => {
  const previousItems = Array.isArray(previous) ? previous : [];
  return next.map((value) => {
    if (!options.find((option) => option.value === value)?.sided) return value;
    const kept = previousItems.find(
      (item): item is UsSidedAnswer =>
        typeof item !== "string" && item.value === value,
    );
    return kept ?? { value, side: null };
  });
};

const SidePicker: FC<{
  option: UsOption;
  answer: UsSidedAnswer;
  onChange: (side: UsSide | null) => void;
  disabled: boolean;
}> = ({ option, answer, onChange, disabled }) => (
  <Box
    display="flex"
    flexDirection="column"
    role="group"
    aria-label={`${option.label} side`}
  >
    <FieldLabel>{`${option.label}: side`}</FieldLabel>
    <EnumChipSelect
      options={SIDE_OPTIONS}
      selected={answer.side}
      onChange={(side) => onChange(side as UsSide | null)}
      disabled={disabled}
    />
  </Box>
);

const UsField: FC<{
  field: UsFieldDef;
  findings: UsWorksheetData["findings"];
  onChange: (id: string, value: UsFindingValue) => void;
  disabled: boolean;
  sectionLabel?: string;
}> = ({ field, findings, onChange, disabled, sectionLabel }) => {
  const value = findings[field.id];
  const otherKey = otherTextKey(field.id);

  if (field.kind === "number") {
    return (
      <>
        <FieldLabel>{field.label}</FieldLabel>
        <NumericInput
          value={typeof value === "number" ? value : null}
          onChange={(next) =>
            onChange(
              field.id,
              next === null || Number.isNaN(next) ? null : next,
            )
          }
          unit={field.unit}
          ariaLabel={field.label}
          disabled={disabled}
        />
      </>
    );
  }

  if (field.kind === "text") {
    return (
      <>
        {field.label !== sectionLabel && <FieldLabel>{field.label}</FieldLabel>}
        <FreeText
          value={value}
          onChange={(next) => onChange(field.id, next)}
          label={field.label}
          disabled={disabled}
        />
      </>
    );
  }

  const selected = selectedValues(value);
  const chosen = field.options.filter((option) =>
    selected.includes(option.value),
  );

  return (
    <>
      {field.label !== sectionLabel && <FieldLabel>{field.label}</FieldLabel>}
      {field.kind === "single" && (
        <EnumChipSelect
          options={[...field.options]}
          selected={selected[0] ?? null}
          onChange={(next) => onChange(field.id, next)}
          disabled={disabled}
        />
      )}
      {field.kind === "dropdown" && (
        <TextField
          select
          size="small"
          variant="outlined"
          value={selected[0] ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(field.id, e.target.value || null)
          }
          disabled={disabled}
          inputProps={{ "aria-label": field.label }}
          style={{ maxWidth: 360 }}
        >
          <MenuItem value="">
            <em>Not answered</em>
          </MenuItem>
          {field.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {field.kind === "multi" && (
        <MultiChipSelect
          options={field.options}
          selected={selected}
          onChange={(next) =>
            onChange(field.id, toMultiValue(field.options, value, next))
          }
          disabled={disabled}
          label={field.label}
        />
      )}
      {chosen.map((option) => (
        <Box
          key={option.value}
          display="flex"
          flexDirection="column"
          style={{ gap: 4, paddingLeft: 16 }}
        >
          {option.sided && Array.isArray(value) && (
            <SidePicker
              option={option}
              answer={
                value.find(
                  (item): item is UsSidedAnswer =>
                    typeof item !== "string" && item.value === option.value,
                ) ?? { value: option.value, side: null }
              }
              onChange={(side) =>
                onChange(
                  field.id,
                  value.map((item) =>
                    typeof item !== "string" && item.value === option.value
                      ? { ...item, side }
                      : item,
                  ),
                )
              }
              disabled={disabled}
            />
          )}
          {option.revealsText && (
            <FreeText
              value={findings[otherKey]}
              onChange={(text) => onChange(otherKey, text)}
              label={`${field.label}, ${option.label.toLowerCase()}`}
              disabled={disabled}
            />
          )}
          {option.reveals?.map((revealed) => (
            <UsFieldBlock
              key={revealed.id}
              field={revealed}
              findings={findings}
              onChange={onChange}
              disabled={disabled}
            />
          ))}
        </Box>
      ))}
    </>
  );
};

const UsFieldBlock: FC<{
  field: UsFieldDef;
  findings: UsWorksheetData["findings"];
  onChange: (id: string, value: UsFindingValue) => void;
  disabled: boolean;
  sectionLabel?: string;
}> = (props) => {
  const { field, findings } = props;
  if (field.showWhen) {
    const shown = selectedValues(findings[field.showWhen.field]);
    if (!shown.some((value) => field.showWhen?.in.includes(value))) return null;
  }
  return (
    <Box display="flex" flexDirection="column">
      {field.heading && <FieldHeading>{field.heading}</FieldHeading>}
      <UsField {...props} />
    </Box>
  );
};

export const UsWorksheet: FC<{
  data: UsWorksheetData;
  onChange: (data: UsWorksheetData) => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
}> = ({ data, onChange, readOnly, imageGallery }) => {
  const definition = usDefinitionFor(data.examType);
  if (!definition) return null;

  const update = (next: UsWorksheetData) => onChange(normalizeUs(next));
  const setFinding = (id: string, value: UsFindingValue) =>
    update({ ...data, findings: { ...data.findings, [id]: value } });
  const indications = definition.otherIndication
    ? [...definition.indications, OTHER]
    : definition.indications;
  const normalLabel = normalImpressionLabel(definition);
  const instances = visibleInstances(definition, data.findings);

  const renderInstance = (instance: UsSectionInstance) => {
    const limited = data.technicallyLimited[instance.key] ?? false;
    return (
      <WorksheetSection key={instance.key} label={instanceLabel(instance)}>
        {instance.fields.map((field) => (
          <UsFieldBlock
            key={field.id}
            field={field}
            findings={data.findings}
            onChange={setFinding}
            disabled={readOnly}
            sectionLabel={instanceLabel(instance)}
          />
        ))}
        {instance.section.technicallyLimited && (
          <ChipRow>
            <Chip
              label="Technically limited"
              variant="outlined"
              aria-pressed={limited}
              style={chipStyle(limited)}
              onClick={() =>
                update({
                  ...data,
                  technicallyLimited: {
                    ...data.technicallyLimited,
                    [instance.key]: !limited,
                  },
                })
              }
              disabled={readOnly}
            />
          </ChipRow>
        )}
        {limited && instance.reasonKey && (
          <FreeText
            value={data.findings[instance.reasonKey]}
            onChange={(text) =>
              instance.reasonKey && setFinding(instance.reasonKey, text)
            }
            label={`${instanceLabel(instance)}, technically limited`}
            disabled={readOnly}
          />
        )}
      </WorksheetSection>
    );
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <AllNormalToggle
        isAllNormal={isUsAllNormal(data)}
        onToggle={() => update(toggleUsAllNormal(data))}
        disabled={readOnly}
        description={
          normalLabel
            ? `Set every finding to normal and the impression to ${normalLabel}. Edit exceptions below.`
            : "Set every finding and the impression to normal. Edit exceptions below."
        }
      />

      {instances
        .filter(({ section }) => section.beforeIndication)
        .map(renderInstance)}

      <WorksheetSection label="Indication">
        {definition.fixedIndication ? (
          <Typography>
            {definition.indications.map(({ label }) => label).join(", ")}
          </Typography>
        ) : (
          <MultiChipSelect
            options={indications}
            selected={data.indication}
            onChange={(indication) => update({ ...data, indication })}
            disabled={readOnly}
            label="Indication"
          />
        )}
        {data.indication.includes(OTHER.value) && (
          <FreeText
            value={data.findings[INDICATION_OTHER_KEY]}
            onChange={(text) => setFinding(INDICATION_OTHER_KEY, text)}
            label="Indication, other"
            disabled={readOnly}
          />
        )}
      </WorksheetSection>

      {definition.views && (
        <WorksheetSection label="Views (optional)">
          <ViewChecklist
            views={definition.views.map((view) => ({
              key: view.value,
              label: view.label,
            }))}
            checked={data.views}
            onChange={(key, checked) =>
              update({ ...data, views: { ...data.views, [key]: checked } })
            }
            disabled={readOnly}
          />
        </WorksheetSection>
      )}

      {instances
        .filter(({ section }) => !section.beforeIndication)
        .map(renderInstance)}

      {imageGallery && (
        <WorksheetSection label="Images / Clips">
          {imageGallery}
        </WorksheetSection>
      )}

      <WorksheetSection label="Notes">
        <NotesField
          value={data.notes}
          onChange={(notes) => update({ ...data, notes })}
          disabled={readOnly}
        />
      </WorksheetSection>
    </Box>
  );
};
